import { useEffect, useRef, useState } from 'react'
import { Boton } from '@ui/primitivas/Boton'
import { Campo } from '@ui/primitivas/Campo'
import { Selector } from '@ui/primitivas/Selector'
import type { AlcanceEntrada, Matriz, Participante } from './tipos'

const TIPOS_DE_CENTRO = ['Casa matriz', 'Sucursal', 'Faena', 'Otro'] as const

/**
 * Mismos máximos que `AlcanceEntrada` en `backend/src/atalaya/api/esquemas.py`.
 * Sin ellos la UI aceptaba textos que la API rechazaba con 422.
 */
export const LIMITES_DE_ALCANCE = {
  razonSocial: 200,
  rut: 20,
  direccion: 300,
  comuna: 100,
  nombreCentro: 200,
  alcance: 300,
  responsable: 200,
  email: 200,
  participanteNombre: 200,
  participanteCargo: 200,
  participantes: 100,
} as const

export function alcanceDesdeMatriz(m: Matriz): AlcanceEntrada {
  return {
    empresa: { ...m.alcance_detalle.empresa },
    centro: { ...m.alcance_detalle.centro },
    alcance: m.alcance_detalle.alcance,
    responsable: { ...m.alcance_detalle.responsable },
    participantes: m.participantes.map((p) => ({ ...p })),
  }
}

export const ALCANCE_VACIO: AlcanceEntrada = {
  empresa: { razon_social: '', rut: '', direccion: '', comuna: '' },
  centro: { nombre: '', tipo: 'Casa matriz', direccion: '', comuna: '' },
  alcance: '',
  responsable: { nombre: '', email: '' },
  participantes: [],
}

export interface FormularioDeAlcanceProps {
  inicial: AlcanceEntrada
  textoDeEnvio: string
  trabajando?: boolean
  error?: string | null
  alEnviar: (alcance: AlcanceEntrada) => void
  /** Avisa si el formulario difiere de lo recibido (para proteger el trabajo sin guardar). */
  alModificar?: (modificado: boolean) => void
}

/** Paso 01 · Alcance de la MIPER según la guía ISP. */
export function FormularioDeAlcance(props: FormularioDeAlcanceProps) {
  const [a, setA] = useState<AlcanceEntrada>(props.inicial)
  const [nuevo, setNuevo] = useState<Participante>({ nombre: '', cargo: '', rol: 'Trabajador/a' })
  const faltantes = [
    !a.empresa.razon_social.trim() ? 'Razón social' : null,
    !a.centro.nombre.trim() ? 'Nombre del centro de trabajo' : null,
    !a.alcance.trim() ? 'Alcance de la MIPER' : null,
    !a.responsable.nombre.trim() ? 'Responsable del proceso IPER' : null,
  ].filter((f): f is string => f !== null)
  const faltan = faltantes.length > 0
  const inicial = useRef(JSON.stringify(props.inicial))
  const { alModificar } = props
  const modificado = JSON.stringify(a) !== inicial.current || Boolean(nuevo.nombre.trim() || nuevo.cargo.trim())
  useEffect(() => { alModificar?.(modificado) }, [modificado, alModificar])

  const agregarParticipante = () => {
    if (!nuevo.nombre.trim() || !nuevo.cargo.trim() || a.participantes.length >= LIMITES_DE_ALCANCE.participantes) return
    setA({ ...a, participantes: [...a.participantes, { ...nuevo, nombre: nuevo.nombre.trim(), cargo: nuevo.cargo.trim() }] })
    setNuevo({ nombre: '', cargo: '', rol: 'Trabajador/a' })
  }

  return (
    <form
      className="grid gap-6"
      onSubmit={(e) => {
        e.preventDefault()
        if (!faltan) props.alEnviar(a)
      }}
    >
      <fieldset className="sst-tarjeta grid gap-3 p-4 sm:p-5">
        <legend className="sst-leyenda">1 · Entidad empleadora</legend>
        <div className="grid gap-3 md:grid-cols-2">
          <Campo etiqueta="Razón social" valor={a.empresa.razon_social} alCambiar={(v) => setA({ ...a, empresa: { ...a.empresa, razon_social: v } })} requerido maxLongitud={LIMITES_DE_ALCANCE.razonSocial} />
          <Campo etiqueta="RUT" valor={a.empresa.rut ?? ''} alCambiar={(v) => setA({ ...a, empresa: { ...a.empresa, rut: v } })} ayuda="Opcional. Sin puntos, con guion: 76123456-0" maxLongitud={LIMITES_DE_ALCANCE.rut} />
          <Campo etiqueta="Dirección" valor={a.empresa.direccion ?? ''} alCambiar={(v) => setA({ ...a, empresa: { ...a.empresa, direccion: v } })} maxLongitud={LIMITES_DE_ALCANCE.direccion} />
          <Campo etiqueta="Comuna" valor={a.empresa.comuna ?? ''} alCambiar={(v) => setA({ ...a, empresa: { ...a.empresa, comuna: v } })} maxLongitud={LIMITES_DE_ALCANCE.comuna} />
        </div>
      </fieldset>

      <fieldset className="sst-tarjeta grid gap-3 p-4 sm:p-5">
        <legend className="sst-leyenda">2 · Centro de trabajo y alcance</legend>
        <div className="grid gap-3 md:grid-cols-2">
          <Campo etiqueta="Nombre del centro de trabajo" valor={a.centro.nombre} alCambiar={(v) => setA({ ...a, centro: { ...a.centro, nombre: v } })} requerido maxLongitud={LIMITES_DE_ALCANCE.nombreCentro} />
          <Selector etiqueta="Tipo de centro" valor={a.centro.tipo} opciones={TIPOS_DE_CENTRO.map((t) => ({ valor: t, etiqueta: t }))} alCambiar={(v) => setA({ ...a, centro: { ...a.centro, tipo: v } })} />
          <Campo etiqueta="Dirección del centro" valor={a.centro.direccion ?? ''} alCambiar={(v) => setA({ ...a, centro: { ...a.centro, direccion: v } })} maxLongitud={LIMITES_DE_ALCANCE.direccion} />
          <Campo etiqueta="Comuna del centro" valor={a.centro.comuna ?? ''} alCambiar={(v) => setA({ ...a, centro: { ...a.centro, comuna: v } })} maxLongitud={LIMITES_DE_ALCANCE.comuna} />
        </div>
        <Campo
          etiqueta="Alcance de la MIPER"
          valor={a.alcance}
          alCambiar={(v) => setA({ ...a, alcance: v })}
          requerido
          maxLongitud={LIMITES_DE_ALCANCE.alcance}
          ayuda={`Una matriz por dirección. Si hay varias en la misma dirección, distingue el área o proceso aquí (p. ej. «Anatomía patológica», «Hospitalización domiciliaria»). Máximo ${LIMITES_DE_ALCANCE.alcance} caracteres (${a.alcance.length} usados).`}
        />
      </fieldset>

      <fieldset className="sst-tarjeta grid gap-3 p-4 sm:p-5">
        <legend className="sst-leyenda">3 · Responsable y participantes</legend>
        <div className="grid gap-3 md:grid-cols-2">
          <Campo etiqueta="Responsable del proceso IPER" valor={a.responsable.nombre} alCambiar={(v) => setA({ ...a, responsable: { ...a.responsable, nombre: v } })} requerido maxLongitud={LIMITES_DE_ALCANCE.responsable} />
          <Campo etiqueta="Email del responsable" tipo="email" valor={a.responsable.email ?? ''} alCambiar={(v) => setA({ ...a, responsable: { ...a.responsable, email: v } })} maxLongitud={LIMITES_DE_ALCANCE.email} />
        </div>
        <p className="text-pequeno text-texto-secundario">
          El DS 44 exige una construcción participativa: registra a quienes aportaron en la identificación de peligros, la evaluación o las medidas. Estos nombres nunca salen de tu organización.
        </p>
        {a.participantes.length ? (
          <ul className="grid gap-1" aria-label="Participantes registrados">
            {a.participantes.map((p, i) => (
              <li key={`${p.nombre}-${i}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-borde px-3 py-2 text-pequeno">
                <span>
                  <strong>{p.nombre}</strong> · {p.cargo} · {p.rol}
                </span>
                <Boton variante="peligro" compacto etiquetaAccesible={`Quitar a ${p.nombre}`} alPulsar={() => setA({ ...a, participantes: a.participantes.filter((_, j) => j !== i) })}>
                  Quitar
                </Boton>
              </li>
            ))}
          </ul>
        ) : null}
        <div className="grid items-end gap-2 md:grid-cols-[1fr_1fr_1fr_auto]">
          <Campo etiqueta="Nombre" valor={nuevo.nombre} alCambiar={(v) => setNuevo({ ...nuevo, nombre: v })} maxLongitud={LIMITES_DE_ALCANCE.participanteNombre} />
          <Campo etiqueta="Cargo" valor={nuevo.cargo} alCambiar={(v) => setNuevo({ ...nuevo, cargo: v })} maxLongitud={LIMITES_DE_ALCANCE.participanteCargo} />
          <Selector
            etiqueta="Rol en la MIPER"
            valor={nuevo.rol}
            opciones={['Trabajador/a', 'Jefatura', 'Comité paritario', 'Prevencionista', 'Otro'].map((r) => ({ valor: r, etiqueta: r }))}
            alCambiar={(v) => setNuevo({ ...nuevo, rol: v })}
          />
          <Boton variante="secundario" alPulsar={agregarParticipante} deshabilitado={!nuevo.nombre.trim() || !nuevo.cargo.trim() || a.participantes.length >= LIMITES_DE_ALCANCE.participantes}>
            Agregar participante
          </Boton>
        </div>
      </fieldset>

      {props.error ? <p role="alert" className="rounded-md bg-critico-fondo p-3 text-critico-texto">{props.error}</p> : null}
      <div className="sst-barra-cierre">
        <p id="requisitos-alcance" className={faltan ? 'text-texto-secundario' : 'text-conforme-texto'}>
          {faltan ? `Para continuar falta: ${faltantes.join(', ')}.` : 'Datos obligatorios completos.'}
        </p>
        <Boton tipo="submit" trabajando={props.trabajando ?? false} deshabilitado={faltan} describidoPor="requisitos-alcance">
          {props.textoDeEnvio}
        </Boton>
      </div>
    </form>
  )
}
